import {Alert, Button, Divider, Group, Stack} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import {AntibodyEnforcedSchemaSpec} from 'antibodies/schema-configuration/AntibodyEnforcedDataModelConstants';
import {enforcedFieldSpecToField} from 'antibodies/schema-configuration/AntibodyEnforcedSchemaSpecUtils';
import {AntibodyComponentSchemaInterfaceId} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {SchemaFieldSelectorsForMigrationQuery_schemaFieldSelectors_SchemaFieldSelectorNode as RegistrySchemaFieldSelector} from 'antibodies/schema-configuration/schema-migration/__generated/SchemaFieldSelectorsForMigrationQuery';
import {FieldMigrationMapping} from 'antibodies/schema-configuration/schema-migration/AntibodySchemaMigrationConstants';
import ConstraintMigrationPreview from 'antibodies/schema-configuration/schema-migration/ConstraintMigrationPreview';
import NamingStrategyMigrationPreview from 'antibodies/schema-configuration/schema-migration/NamingStrategyMigrationPreview';
import NewFieldsMigrationPreview from 'antibodies/schema-configuration/schema-migration/NewFieldsMigrationPreview';
import {hasAnyUnmappedOptions} from 'antibodies/schema-configuration/schema-migration/newFieldsMigrationUtils';
import {
  getApplicableNamingStrategies,
  getShouldAddNamingStrategies,
} from 'antibodies/schema-configuration/schema-migration/schemaMigrationNamingStrategies';
import {
  ClientTagSchemaConstraint,
  RegistryLabelingStrategy,
  TagSchemaConstraint,
} from 'constants/RegistryConstants';
import {NamingStrategy} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';

function useNamingStrategiesToAdd(
  enforcedNamingStrategySpec: AntibodyEnforcedSchemaSpec['enforcedNamingStrategySpec'],
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null,
  hasNameTemplate: boolean
): {
  shouldAddNamingStrategies: boolean;
  applicableNamingStrategies: Array<NamingStrategy>;
} {
  const shouldAddNamingStrategies = useMemo(
    () =>
      enforcedNamingStrategySpec != null &&
      getShouldAddNamingStrategies(enforcedNamingStrategySpec, existingNamingStrategies),
    [enforcedNamingStrategySpec, existingNamingStrategies]
  );
  // The strategies compatible with this schema's name-template state — these are the ones that will
  // be added to the schema. Labeling strategies aren't user-configurable in this flow: whichever
  // strategies apply are applied automatically.
  const applicableNamingStrategies = useMemo(
    () =>
      enforcedNamingStrategySpec != null
        ? getApplicableNamingStrategies(enforcedNamingStrategySpec, hasNameTemplate)
        : [],
    [enforcedNamingStrategySpec, hasNameTemplate]
  );

  return {
    shouldAddNamingStrategies,
    applicableNamingStrategies,
  };
}

export interface MigrationPreviewBodyProps {
  componentTypeName: string;
  componentInterfaceId: AntibodyComponentSchemaInterfaceId;
  registryId: string;
  enforcedSchemaSpec: AntibodyEnforcedSchemaSpec;
  existingFields: Array<SchemaField | ClientSchemaField>;
  existingConstraint: ClientTagSchemaConstraint | TagSchemaConstraint | null;
  existingNamingStrategies: Array<RegistryLabelingStrategy> | null;
  hasNameTemplate: boolean;
  folderItemType: string | null;
  fieldMigrationMappings: Array<FieldMigrationMapping>;
  onMappingsChange: React.Dispatch<React.SetStateAction<Array<FieldMigrationMapping>>>;
  allRegistrySelectors: Array<RegistrySchemaFieldSelector>;
  onOpenOptionMapping: (destFieldName: string) => void;
  isApplying: boolean;
  onApply: (options: {
    namingStrategiesToAdd?: Array<NamingStrategy>;
    fieldMigrationMappings: Array<FieldMigrationMapping>;
  }) => void | Promise<void>;
  onClose: () => void;
}

/**
 * The main preview body of the migration confirmation modal: the enforced-changes alert, the new
 * fields / constraint / naming-strategy sections, and the Apply/Cancel footer.
 */
export default function MigrationPreviewBody({
  componentTypeName,
  componentInterfaceId,
  registryId,
  enforcedSchemaSpec,
  existingFields,
  existingConstraint,
  existingNamingStrategies,
  hasNameTemplate,
  folderItemType,
  fieldMigrationMappings,
  onMappingsChange,
  allRegistrySelectors,
  onOpenOptionMapping,
  isApplying,
  onApply,
  onClose,
}: MigrationPreviewBodyProps): JSX.Element {
  const {enforcedFieldSpecs, enforcedConstraintSpec, enforcedNamingStrategySpec} = enforcedSchemaSpec;
  const enforcedFieldsToCreate = enforcedFieldSpecs.map((enforcedFieldSpec) =>
    enforcedFieldSpecToField(componentInterfaceId, enforcedFieldSpec, registryId)
  );
  const {shouldAddNamingStrategies, applicableNamingStrategies} = useNamingStrategiesToAdd(
    enforcedNamingStrategySpec,
    existingNamingStrategies,
    hasNameTemplate
  );

  const handleApplyClick = useCallback((): void => {
    onApply({
      ...(shouldAddNamingStrategies ? {namingStrategiesToAdd: applicableNamingStrategies} : {}),
      fieldMigrationMappings,
    });
  }, [onApply, fieldMigrationMappings, applicableNamingStrategies, shouldAddNamingStrategies]);

  const hasNoApplicableStrategies = shouldAddNamingStrategies && applicableNamingStrategies.length === 0;
  // Block submission while any migratable field still has source options that need mapping.
  const hasUnmappedOptions = hasAnyUnmappedOptions(
    enforcedFieldsToCreate,
    enforcedFieldSpecs,
    existingFields,
    fieldMigrationMappings,
    allRegistrySelectors
  );
  const isApplyDisabled = isApplying || hasNoApplicableStrategies || hasUnmappedOptions;

  return (
    <Stack spacing="lg">
      <Alert>
        Applying the {componentTypeName} protein component type makes the following changes to this schema.
        This action cannot be undone.
      </Alert>

      <NewFieldsMigrationPreview
        fieldsToCreate={enforcedFieldsToCreate}
        enforcedFieldSpecs={enforcedFieldSpecs}
        existingFields={existingFields}
        fieldMigrationMappings={fieldMigrationMappings}
        onMappingsChange={onMappingsChange}
        allRegistrySelectors={allRegistrySelectors}
        onOpenOptionMapping={onOpenOptionMapping}
      />

      {enforcedConstraintSpec != null && (
        <ConstraintMigrationPreview
          existingConstraint={existingConstraint}
          enforcedConstraintSpec={enforcedConstraintSpec}
          enforcedFields={enforcedFieldsToCreate}
          folderItemType={folderItemType}
        />
      )}

      {enforcedNamingStrategySpec != null && (
        <NamingStrategyMigrationPreview
          enforcedNamingStrategySpec={enforcedNamingStrategySpec}
          existingNamingStrategies={existingNamingStrategies}
          applicableNamingStrategies={applicableNamingStrategies}
        />
      )}

      {hasNoApplicableStrategies && (
        <Alert variant="error">
          None of the required labeling strategies are available for this schema. Add a name template or
          enable the required strategies on this deployment.
        </Alert>
      )}

      <Divider />

      <Group position="right">
        <Button variant="secondary" onClick={onClose} disabled={isApplying}>
          Cancel
        </Button>
        <Button onClick={handleApplyClick} loading={isApplying} disabled={isApplyDisabled}>
          Apply
        </Button>
      </Group>
    </Stack>
  );
}
